import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

const fmt = (n) => {
  if (!n) return null;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)   return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

/**
 * Compact "Client Memory" card shown in SessionDetail.
 * Only renders if the client has history beyond the current session.
 */
const ClientMemoryCard = ({ clientPhone, currentSessionId }) => {
  const navigate = useNavigate();
  const [memory, setMemory] = useState(null);
  const [open, setOpen]     = useState(false);

  useEffect(() => {
    if (!clientPhone) return;
    api.get(`/crm/clients/${encodeURIComponent(clientPhone)}/timeline`)
      .then(r => {
        const { sessions, stats } = r.data;
        const otherSessions = sessions.filter(s => s._id !== currentSessionId);
        if (!otherSessions.length && stats.total_visits <= 1) return; // no prior history

        // Build memory card data
        const allLeads = sessions.flatMap(s => s.leads || []);
        const products = [...new Set(allLeads.map(l => l.product_name).filter(Boolean))];
        const values   = allLeads.map(l => l.estimated_value).filter(v => v > 0);
        const minBudget = values.length ? Math.min(...values) : 0;
        const maxBudget = values.length ? Math.max(...values) : 0;

        // Last meaningful concern: most recent parked/loss reason
        const lastConcern = allLeads
          .filter(l => l.reason)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0]?.reason || null;

        setMemory({
          totalVisits:   stats.total_visits,
          totalSessions: stats.total_sessions,
          products:      products.slice(0, 3),
          minBudget,
          maxBudget,
          lastConcern,
          openLeads:     stats.open_leads,
        });
      })
      .catch(() => {});
  }, [clientPhone, currentSessionId]);

  if (!memory) return null;

  return (
    <div
      className="bg-teal-50 border-b border-teal-200 px-4 py-2"
      data-testid="client-memory-card"
    >
      <button
        className="w-full flex items-center justify-between text-left"
        onClick={() => setOpen(o => !o)}
        data-testid="memory-card-toggle"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-teal-700">
            <MapPin className="w-3 h-3" />
            <span className="text-xs font-semibold">{memory.totalVisits} visits</span>
          </div>
          {memory.products.length > 0 && (
            <span className="text-xs text-teal-600">
              · {memory.products.join(', ')}
            </span>
          )}
          {memory.maxBudget > 0 && (
            <span className="text-xs text-teal-600 font-medium">
              · {memory.minBudget === memory.maxBudget ? fmt(memory.maxBudget) : `${fmt(memory.minBudget)}–${fmt(memory.maxBudget)}`}
            </span>
          )}
        </div>
        {open
          ? <ChevronUp className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
          : <ChevronDown className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
        }
      </button>

      {open && (
        <div className="mt-2 space-y-1 text-xs text-teal-700 border-t border-teal-200 pt-2">
          <p><span className="font-semibold">Sessions:</span> {memory.totalSessions}</p>
          {memory.products.length > 0 && (
            <p><span className="font-semibold">Interested in:</span> {memory.products.join(', ')}</p>
          )}
          {memory.maxBudget > 0 && (
            <p>
              <span className="font-semibold">Budget range:</span>{' '}
              {memory.minBudget === memory.maxBudget
                ? fmt(memory.maxBudget)
                : `${fmt(memory.minBudget)} – ${fmt(memory.maxBudget)}`}
            </p>
          )}
          {memory.lastConcern && (
            <p>
              <span className="font-semibold">Last concern:</span>{' '}
              <span className="italic">"{memory.lastConcern}"</span>
            </p>
          )}
          <button
            onClick={() => navigate(`/client-timeline/${encodeURIComponent(clientPhone)}`)}
            className="mt-1 text-[10px] text-teal-600 underline underline-offset-2"
            data-testid="view-full-timeline-btn"
          >
            View full history →
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientMemoryCard;
